import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, AlertTriangle, RefreshCw, Globe, Mic, Volume2, Square, MicOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../services/api';

const TRANSLATIONS = {
  en: {
    title: "Afya Health Assistant",
    placeholder: "Describe your symptoms...",
    send: "Send",
    newChat: "New Chat",
    bookDoctor: "Find a Doctor",
    emergency: "Emergency",
    typing: "Afya is typing...",
    disclaimer: "This is not a medical diagnosis. Please consult a healthcare professional.",
    welcome: "Hello! I'm Afya, your AI health assistant. How can I help you today?",
    language: "Language",
    english: "English",
    swahili: "Kiswahili",
    recording: "Recording...",
    transcribing: "Transcribing...",
    voiceHint: "Tap mic to speak",
    speakResponse: "Read aloud",
    stopSpeaking: "Stop reading"
  },
  sw: {
    title: "Msaidizi wa Afya",
    placeholder: "Eleza dalili zako...",
    send: "Tuma",
    newChat: "Mazungumzo Mapya",
    bookDoctor: "Tafuta Daktari",
    emergency: "Dharura",
    typing: "Afya anaandika...",
    disclaimer: "Hii si utambuzi wa kimatibabu. Tafadhali wasiliana na mtaalamu wa afya.",
    welcome: "Habari! Mimi ni Afya, msaidizi wako wa afya wa AI. Ninawezaje kukusaidia leo?",
    language: "Lugha",
    english: "Kiingereza",
    swahili: "Kiswahili",
    recording: "Inarekodiwa...",
    transcribing: "Inatafsiriwa...",
    voiceHint: "Gusa maikrofoni kuzungumza",
    speakResponse: "Soma kwa sauti",
    stopSpeaking: "Acha kusoma"
  }
};

export default function SymptomCheckerChat({ onBookDoctor }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [language, setLanguage] = useState('en');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [urgencyLevel, setUrgencyLevel] = useState('low');
  const [recommendedSpecialty, setRecommendedSpecialty] = useState(null);
  const [showBookingPrompt, setShowBookingPrompt] = useState(false);
  
  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const messagesEndRef = useRef(null);
  const t = TRANSLATIONS[language];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      if (messages.length === 0 || (messages.length === 1 && messages[0].id === 'welcome')) {
        setMessages([{
          id: 'welcome',
          type: 'assistant',
          content: t.welcome,
          timestamp: new Date()
        }]);
      }
    }
  }, [language, isOpen, t.welcome]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        type: 'assistant',
        content: t.welcome,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, language]);

  // Cleanup speech on unmount or close
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const handleSend = async (messageText) => {
    const text = messageText || inputValue;
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await api.post('/ai/symptom-checker/chat', {
        message: text,
        session_id: sessionId,
        language: language
      });

      const data = response.data;
      setSessionId(data.session_id);
      setUrgencyLevel(data.urgency_level);
      setRecommendedSpecialty(data.recommended_specialty);
      setShowBookingPrompt(data.show_booking_prompt);

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: data.response,
        urgencyLevel: data.urgency_level,
        hasRedFlags: data.has_red_flags,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (data.has_red_flags) {
        const emergencyMessage = {
          id: Date.now() + 2,
          type: 'emergency',
          content: language === 'sw'
            ? 'Kulingana na dalili zako, tafadhali tafuta huduma ya dharura ya kimatibabu. Dharura: 999'
            : 'Based on your symptoms, please seek immediate medical attention. Emergency: 999',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, emergencyMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: language === 'sw'
          ? 'Samahani, kuna tatizo. Tafadhali jaribu tena.'
          : 'Sorry, there was an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = async () => {
    if (sessionId) {
      try {
        await api.post(`/ai/symptom-checker/reset?session_id=${sessionId}`);
      } catch (error) {
        console.error('Reset error:', error);
      }
    }
    stopSpeaking();
    setMessages([{
      id: 'welcome',
      type: 'assistant',
      content: t.welcome,
      timestamp: new Date()
    }]);
    setSessionId(null);
    setUrgencyLevel('low');
    setRecommendedSpecialty(null);
    setShowBookingPrompt(false);
  };

  const handleBookDoctor = () => {
    if (onBookDoctor) {
      onBookDoctor(recommendedSpecialty);
    }
    window.location.href = `/directory${recommendedSpecialty ? `?specialty=${encodeURIComponent(recommendedSpecialty)}` : ''}`;
  };

  // ========== VOICE: Recording ==========
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size > 0) {
          await transcribeAudio(audioBlob);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access error:', err);
    }
  }, [language]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await api.post(`/ai/voice/transcribe?language=${language}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const text = response.data.text;
      if (text && text.trim()) {
        setInputValue(text);
        // Auto-send the transcribed text
        await handleSendDirect(text);
      }
    } catch (error) {
      console.error('Transcription error:', error);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Direct send without relying on inputValue state
  const handleSendDirect = async (text) => {
    if (!text.trim() || isLoading) return;
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await api.post('/ai/symptom-checker/chat', {
        message: text,
        session_id: sessionId,
        language: language
      });
      const data = response.data;
      setSessionId(data.session_id);
      setUrgencyLevel(data.urgency_level);
      setRecommendedSpecialty(data.recommended_specialty);
      setShowBookingPrompt(data.show_booking_prompt);

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: data.response,
        urgencyLevel: data.urgency_level,
        hasRedFlags: data.has_red_flags,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);

      if (data.has_red_flags) {
        setMessages(prev => [...prev, {
          id: Date.now() + 2,
          type: 'emergency',
          content: language === 'sw'
            ? 'Kulingana na dalili zako, tafadhali tafuta huduma ya dharura ya kimatibabu. Dharura: 999'
            : 'Based on your symptoms, please seek immediate medical attention. Emergency: 999',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'error',
        content: language === 'sw' ? 'Samahani, kuna tatizo.' : 'Sorry, there was an error.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== VOICE: Text-to-Speech ==========
  const speakText = useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'sw' ? 'sw-KE' : 'en-US';
    utterance.rate = 0.9;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  }, [language]);

  const stopSpeaking = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const getUrgencyColor = (level) => {
    switch (level) {
      case 'emergency': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        data-testid="symptom-checker-toggle"
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-white shadow-lg flex items-center justify-center transition-all ${isOpen ? 'scale-0' : 'scale-100'}`}
        aria-label="Open symptom checker"
      >
        <MessageCircle className="h-7 w-7" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
             data-testid="symptom-checker-window">
          {/* Header */}
          <div className="bg-[#0F2A44] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#2DD4BF] flex items-center justify-center">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{t.title}</h3>
                <div className="flex items-center gap-2 text-xs text-white/70">
                  <span className={`w-2 h-2 rounded-full ${getUrgencyColor(urgencyLevel)}`}></span>
                  <span>Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="chat-language-btn"
                  title={t.language}
                >
                  <Globe className="h-4 w-4" />
                </button>
                {showLanguageMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg py-1 min-w-[120px] z-10">
                    <button
                      onClick={() => { setLanguage('en'); setShowLanguageMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-100 ${language === 'en' ? 'text-[#2DD4BF] font-medium' : 'text-slate-700'}`}
                    >
                      English
                    </button>
                    <button
                      onClick={() => { setLanguage('sw'); setShowLanguageMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-100 ${language === 'sw' ? 'text-[#2DD4BF] font-medium' : 'text-slate-700'}`}
                    >
                      Kiswahili
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleNewChat}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="chat-new-btn"
                title={t.newChat}
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button
                onClick={() => { setIsOpen(false); stopSpeaking(); }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Disclaimer Banner */}
          <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 text-xs text-amber-800">
            {t.disclaimer}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-[#2DD4BF] text-white rounded-br-md'
                      : message.type === 'emergency'
                      ? 'bg-red-100 text-red-800 border border-red-200'
                      : message.type === 'error'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}
                >
                  {message.type === 'emergency' && (
                    <AlertTriangle className="h-5 w-5 mb-2" />
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs opacity-60">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {/* TTS button for assistant messages */}
                    {message.type === 'assistant' && message.id !== 'welcome' && (
                      <button
                        onClick={() => isSpeaking ? stopSpeaking() : speakText(message.content)}
                        className="ml-2 p-1 rounded-full hover:bg-black/5 transition-colors"
                        data-testid={`speak-msg-${message.id}`}
                        title={isSpeaking ? t.stopSpeaking : t.speakResponse}
                      >
                        <Volume2 className={`h-3.5 w-3.5 ${isSpeaking ? 'text-[#2DD4BF]' : 'opacity-40 hover:opacity-70'}`} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl px-4 py-3 rounded-bl-md">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    <span>{t.typing}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Voice status indicators */}
            {isRecording && (
              <div className="flex justify-center">
                <div className="bg-red-50 border border-red-200 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-red-600 animate-pulse">
                  <Mic className="h-4 w-4" />
                  {t.recording}
                </div>
              </div>
            )}
            {isTranscribing && (
              <div className="flex justify-center">
                <div className="bg-blue-50 border border-blue-200 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-blue-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t.transcribing}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Booking Prompt */}
          {showBookingPrompt && recommendedSpecialty && (
            <div className="px-4 py-3 bg-[#2DD4BF]/10 border-t border-[#2DD4BF]/20">
              <p className="text-sm text-slate-700 mb-2">
                {language === 'sw'
                  ? `Ninapendekeza uone mtaalamu wa ${recommendedSpecialty}.`
                  : `I recommend seeing a ${recommendedSpecialty} specialist.`
                }
              </p>
              <Button
                onClick={handleBookDoctor}
                data-testid="chat-book-doctor-btn"
                className="w-full bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-semibold"
              >
                {t.bookDoctor}
              </Button>
            </div>
          )}

          {/* Input Area */}
          <div className="p-3 border-t border-slate-200">
            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex gap-2 items-center"
            >
              {/* Voice Input Button */}
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isLoading || isTranscribing}
                data-testid="chat-voice-btn"
                className={`flex-shrink-0 rounded-full w-10 h-10 flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : isTranscribing
                    ? 'bg-blue-100 text-blue-500'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700'
                }`}
                title={isRecording ? t.recording : t.voiceHint}
              >
                {isRecording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>

              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t.placeholder}
                className="flex-1 rounded-full h-10 text-sm"
                disabled={isLoading || isRecording || isTranscribing}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={!inputValue.trim() || isLoading || isRecording}
                data-testid="chat-send-btn"
                className="rounded-full w-10 h-10 p-0 bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 flex-shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
